import {
  requestMapRecenter,
  setSelectedDeviceId,
} from 'features/Integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  buildBuoyChartOption,
  buildBuoyDeviceChartModel,
  getBuoyDevices,
  getBuoyFieldConfig,
  getFirstAvailableBuoyField,
  getSelectedBuoyDevices,
  resolveBuoyObservationIdFromParams,
} from '../../Buoy/graph/buoyGraph.utils';
import { formatGraphTimeRangeLabel } from '../utils/deviceGraphHelpers.utils';
import { bindTooltipCanvasClick } from '../utils/graphInteraction.utils';
import * as usgsEventUtils from '../utils/usgsEvent.utils';

export const useBuoyGraphBuilder = ({
  title = 'Buoy Devices',
  devices = [],
  selectedDeviceMap = {},
  selectedDataType,
  unitType,
  setUnitType,
  startTime = undefined,
  endTime = undefined,
  historyDataLoading = false,
  dispatch = undefined,
  navigate = undefined,
  location = undefined,
  onSelectedDeviceIndex = undefined,
  onSelectDataType = undefined,
  onGraphDatasheetSelect = undefined,
  setShowCurrentDevice = undefined,
}) => {
  const tooltipParamsRef = useRef(null);
  const zrenderCleanupRef = useRef(null);
  const selectGraphDataPointRef = useRef(null);

  useEffect(
    () => () => {
      zrenderCleanupRef.current?.();
      zrenderCleanupRef.current = null;
    },
    [],
  );

  const firstSelectedDevice = useMemo(() => {
    const buoyDevices = getBuoyDevices(devices);
    const firstSelectedId = Object.keys(selectedDeviceMap || {}).find(
      (id) => selectedDeviceMap[id] && buoyDevices.some((device) => String(device.id) === id),
    );
    return firstSelectedId
      ? (devices.find((device) => String(device.id) === firstSelectedId) ?? null)
      : null;
  }, [devices, selectedDeviceMap]);

  const selectedDevices = useMemo(
    () => getSelectedBuoyDevices(devices, selectedDeviceMap),
    [devices, selectedDeviceMap],
  );
  const selectedField = useMemo(() => getBuoyFieldConfig(selectedDataType), [selectedDataType]);
  const fallbackField = useMemo(
    () =>
      getFirstAvailableBuoyField({
        devices,
        selectedDeviceMap,
        unitType,
        startTime,
        endTime,
      }),
    [devices, selectedDeviceMap, unitType, startTime, endTime],
  );
  const timeRangeLabel = useMemo(
    () => formatGraphTimeRangeLabel(startTime, endTime),
    [startTime, endTime],
  );

  const { labels, datasets } = useMemo(
    () =>
      buildBuoyDeviceChartModel({
        devices,
        selectedDeviceMap,
        selectedDataType,
        unitType,
        startTime,
        endTime,
      }),
    [devices, selectedDeviceMap, selectedDataType, unitType, startTime, endTime],
  );

  const option = useMemo(() => {
    const chartOption = buildBuoyChartOption({
      title,
      labels,
      datasets,
      selectedDataType,
      unitType,
      startTime,
      endTime,
    });
    if (!chartOption?.tooltip?.formatter) return chartOption;

    const formatTooltip = chartOption.tooltip.formatter;
    return {
      ...chartOption,
      tooltip: {
        ...chartOption.tooltip,
        formatter: (params) => {
          tooltipParamsRef.current = params;
          return formatTooltip(params);
        },
      },
    };
  }, [title, labels, datasets, selectedDataType, unitType, startTime, endTime]);

  useEffect(() => {
    tooltipParamsRef.current = null;
  }, [datasets]);

  const selectGraphDataPoint = useCallback(
    (params) => {
      if (!params) return;

      const dataset = datasets[params.seriesIndex ?? 0] || {};
      const targetId = dataset?._meta?.id;
      if (!targetId) return;

      const obsID = resolveBuoyObservationIdFromParams(params, dataset?._meta);
      if (obsID == null) return;

      try {
        usgsEventUtils.setUidDidInUrl(targetId, obsID, navigate, location);
      } catch (e) {
        // URL updates are non-critical for graph selection.
      }

      onGraphDatasheetSelect?.(targetId);
      dispatch?.(setSelectedDeviceId(targetId));
      dispatch?.(requestMapRecenter());
      dispatch?.(setSelectedDevice({ id: targetId }));
      setShowCurrentDevice?.(true);

      const globalIndex = (devices || []).findIndex((device) => device.id === targetId);
      if (globalIndex >= 0) onSelectedDeviceIndex?.(globalIndex);
    },
    [
      datasets,
      devices,
      onSelectedDeviceIndex,
      onGraphDatasheetSelect,
      setShowCurrentDevice,
      navigate,
      location,
      dispatch,
    ],
  );

  useEffect(() => {
    selectGraphDataPointRef.current = selectGraphDataPoint;
  }, [selectGraphDataPoint]);

  const onChartClick = useMemo(
    () => (params) => {
      if (!params || params.componentType !== 'series') return;
      selectGraphDataPoint(params);
    },
    [selectGraphDataPoint],
  );

  const onChartReady = useMemo(
    () => (chartInstance) => {
      zrenderCleanupRef.current?.();
      zrenderCleanupRef.current = bindTooltipCanvasClick(
        chartInstance,
        () => tooltipParamsRef.current,
        (tooltipParam) => selectGraphDataPointRef.current?.(tooltipParam),
      );
    },
    [],
  );

  if (!option) {
    if (historyDataLoading) {
      return { option: null, loading: true, loadingMessage: 'Loading device data...' };
    }

    if (firstSelectedDevice) {
      return {
        option: null,
        emptyState: {
          title: `No chart data for ${selectedField.label}`,
          message:
            selectedDevices.length > 1
              ? 'The selected buoys do not have usable values for this field in the current range.'
              : 'This buoy does not have a usable value for this field in the current range.',
          detail:
            fallbackField && fallbackField.value !== selectedField.value ? (
              <>
                {timeRangeLabel ? `${timeRangeLabel} ` : ''}
                Available data was found for <strong>{fallbackField.label}</strong>.
              </>
            ) : (
              timeRangeLabel || 'No buoy metrics have chartable data in this time range.'
            ),
          action:
            fallbackField && fallbackField.value !== selectedField.value
              ? {
                  key: 'switch-metric',
                  label: `Switch to ${fallbackField.label}`,
                  className: 'px-3 py-2',
                  onClick: () => onSelectDataType?.(fallbackField.value),
                }
              : undefined,
        },
      };
    }

    return { option: null, emptyMessage: 'No device selected' };
  }

  return {
    option,
    datasets,
    onChartClick,
    onChartReady,
    controlsLeft: [
      {
        key: 'units',
        label: `Units: ${unitType ? 'Metric' : 'Imperial'}`,
        className: 'm-2 px-2 py-1',
        onClick: () => setUnitType?.((prev) => !prev),
      },
    ],
  };
};
